import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import Swal from "sweetalert2";
import classnames from "classnames";

import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import { startImpersonation } from "../../../../services/authService";
import { getTenant } from "../../../../services/tenantService";
import {
  archiveTenantProfessional,
  createTenantProfessional,
  getTenantProfessionals,
  getTenantUnits,
  getTenantUsers,
  impersonateUser,
  inviteTenantProfessional,
  inviteTenantUser,
  restoreTenantProfessional,
  revokeTenantUserMembership,
  TenantTeamProfessional,
  TenantTeamUser,
  updateTenantProfessional,
  updateTenantUser,
} from "../../../../services/superAdmin/adminTenantTeamService";

type TeamTab = "users" | "professionals";

const INVITABLE_ROLES = ["manager", "receptionist", "professional", "sdr"];

const SuperAdminTenantTeam = () => {
  const { t, i18n } = useTranslation();
  const { tenantId: tenantIdParam } = useParams<{ tenantId: string }>();
  const tenantId = Number(tenantIdParam);

  const [tenantName, setTenantName] = useState<string>("");
  const [tab, setTab] = useState<TeamTab>("users");
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<TenantTeamUser[]>([]);
  const [professionals, setProfessionals] = useState<TenantTeamProfessional[]>(
    []
  );
  const [includeInactive, setIncludeInactive] = useState(false);
  const [unitOptions, setUnitOptions] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const [busyId, setBusyId] = useState<number | null>(null);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    email: "",
    name: "",
    role: "manager",
    units: [] as number[],
    professional_id: "" as string,
  });

  const [proModalOpen, setProModalOpen] = useState(false);
  const [editingPro, setEditingPro] = useState<TenantTeamProfessional | null>(
    null
  );
  const [proForm, setProForm] = useState({
    name: "",
    email: "",
    color_hex: "#4B38B3",
  });

  useEffect(() => {
    document.title = t("admin.tenantTeam.pageTitle");
  }, [t, i18n.language]);

  const loadTenant = useCallback(async () => {
    if (!Number.isFinite(tenantId)) {
      return;
    }
    try {
      const [tenantResponse, unitsResponse] = await Promise.all([
        getTenant(tenantId),
        getTenantUnits(tenantId),
      ]);
      setTenantName(tenantResponse.data.name);
      setUnitOptions(unitsResponse.data ?? []);
    } catch {
      toast.error(t("admin.tenantTeam.toast.tenantLoadError"));
    }
  }, [tenantId, t]);

  const loadUsers = useCallback(async () => {
    if (!Number.isFinite(tenantId)) {
      return;
    }
    setLoading(true);
    try {
      const response = await getTenantUsers(tenantId, { per_page: 100 });
      setUsers(response.data ?? []);
      const derivedUnits = new Map<number, string>();
      (response.data ?? []).forEach((user) => {
        user.units.forEach((unit) => derivedUnits.set(unit.id, unit.name));
      });
      if (derivedUnits.size > 0) {
        setUnitOptions(
          Array.from(derivedUnits.entries()).map(([id, name]) => ({ id, name }))
        );
      }
    } catch {
      toast.error(t("admin.tenantTeam.toast.usersLoadError"));
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [tenantId, t]);

  const loadProfessionals = useCallback(async () => {
    if (!Number.isFinite(tenantId)) {
      return;
    }
    setLoading(true);
    try {
      const response = await getTenantProfessionals(tenantId, {
        per_page: 100,
        include_inactive: includeInactive,
      });
      setProfessionals(response.data ?? []);
    } catch {
      toast.error(t("admin.tenantTeam.toast.professionalsLoadError"));
      setProfessionals([]);
    } finally {
      setLoading(false);
    }
  }, [tenantId, includeInactive, t]);

  useEffect(() => {
    void loadTenant();
  }, [loadTenant]);

  useEffect(() => {
    if (tab === "users") {
      void loadUsers();
    } else {
      void loadProfessionals();
    }
  }, [tab, loadUsers, loadProfessionals]);

  const handleImpersonate = async (user: TenantTeamUser) => {
    const result = await Swal.fire({
      title: t("admin.tenantTeam.impersonate.title"),
      text: t("admin.tenantTeam.impersonate.description", {
        name: user.name,
        email: user.email,
      }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("admin.tenantTeam.impersonate.confirm"),
      cancelButtonText: t("admin.tenantTeam.actions.cancel"),
    });
    if (!result.isConfirmed) {
      return;
    }
    setBusyId(user.id);
    try {
      const response = await impersonateUser(user.id);
      startImpersonation(response.data, tenantName || String(tenantId));
      toast.success(t("admin.tenantTeam.toast.impersonationStarted"));
      window.location.href = "/dashboard";
    } catch {
      toast.error(t("admin.tenantTeam.toast.impersonationError"));
    } finally {
      setBusyId(null);
    }
  };

  const handleRevoke = async (user: TenantTeamUser) => {
    if (user.roles.includes("owner")) {
      toast.error(t("admin.tenantTeam.toast.cannotRevokeOwner"));
      return;
    }
    const result = await Swal.fire({
      title: t("admin.tenantTeam.revoke.title"),
      text: t("admin.tenantTeam.revoke.description", { name: user.name }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("admin.tenantTeam.revoke.confirm"),
      cancelButtonText: t("admin.tenantTeam.actions.cancel"),
    });
    if (!result.isConfirmed) {
      return;
    }
    setBusyId(user.id);
    try {
      await revokeTenantUserMembership(tenantId, user.id);
      toast.success(t("admin.tenantTeam.toast.revoked"));
      await loadUsers();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error?.message ||
          t("admin.tenantTeam.toast.revokeError")
      );
    } finally {
      setBusyId(null);
    }
  };

  const handleInviteSubmit = async () => {
    if (!inviteForm.email || !inviteForm.name || inviteForm.units.length === 0) {
      toast.error(t("admin.tenantTeam.invite.validation"));
      return;
    }
    if (inviteForm.role === "professional" && !inviteForm.professional_id) {
      toast.error(t("admin.tenantTeam.invite.professionalRequired"));
      return;
    }
    setBusyId(-1);
    try {
      await inviteTenantUser(tenantId, {
        email: inviteForm.email.trim(),
        name: inviteForm.name.trim(),
        role: inviteForm.role,
        units: inviteForm.units,
        professional_id:
          inviteForm.role === "professional"
            ? Number(inviteForm.professional_id)
            : null,
      });
      toast.success(t("admin.tenantTeam.toast.invited"));
      setInviteOpen(false);
      setInviteForm({
        email: "",
        name: "",
        role: "manager",
        units: unitOptions[0] ? [unitOptions[0].id] : [],
        professional_id: "",
      });
      await loadUsers();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error?.message ||
          t("admin.tenantTeam.toast.inviteError")
      );
    } finally {
      setBusyId(null);
    }
  };

  const openCreateProfessional = () => {
    setEditingPro(null);
    setProForm({ name: "", email: "", color_hex: "#4B38B3" });
    setProModalOpen(true);
  };

  const openEditProfessional = (professional: TenantTeamProfessional) => {
    setEditingPro(professional);
    setProForm({
      name: professional.name,
      email: professional.email || "",
      color_hex: professional.color_hex || "#4B38B3",
    });
    setProModalOpen(true);
  };

  const handleSaveProfessional = async () => {
    if (!proForm.name.trim()) {
      toast.error(t("admin.tenantTeam.professional.nameRequired"));
      return;
    }
    setBusyId(-2);
    try {
      if (editingPro) {
        await updateTenantProfessional(tenantId, editingPro.id, {
          name: proForm.name.trim(),
          email: proForm.email.trim() || null,
          color_hex: proForm.color_hex,
        });
        toast.success(t("admin.tenantTeam.toast.professionalUpdated"));
      } else {
        await createTenantProfessional(tenantId, {
          name: proForm.name.trim(),
          email: proForm.email.trim() || null,
          color_hex: proForm.color_hex,
        });
        toast.success(t("admin.tenantTeam.toast.professionalCreated"));
      }
      setProModalOpen(false);
      await loadProfessionals();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error?.message ||
          t("admin.tenantTeam.toast.professionalSaveError")
      );
    } finally {
      setBusyId(null);
    }
  };

  const handleArchiveToggle = async (professional: TenantTeamProfessional) => {
    setBusyId(professional.id);
    try {
      if (professional.archived_at || !professional.is_active) {
        await restoreTenantProfessional(tenantId, professional.id);
        toast.success(t("admin.tenantTeam.toast.professionalRestored"));
      } else {
        await archiveTenantProfessional(tenantId, professional.id);
        toast.success(t("admin.tenantTeam.toast.professionalArchived"));
      }
      await loadProfessionals();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error?.message ||
          t("admin.tenantTeam.toast.professionalArchiveError")
      );
    } finally {
      setBusyId(null);
    }
  };

  const handleInviteProfessional = async (
    professional: TenantTeamProfessional
  ) => {
    const result = await Swal.fire({
      title: t("admin.tenantTeam.professionalInvite.title"),
      input: "email",
      inputValue: professional.email || "",
      inputPlaceholder: t("admin.tenantTeam.professionalInvite.placeholder"),
      showCancelButton: true,
      confirmButtonText: t("admin.tenantTeam.professionalInvite.confirm"),
      cancelButtonText: t("admin.tenantTeam.actions.cancel"),
    });
    if (!result.isConfirmed || !result.value) {
      return;
    }
    setBusyId(professional.id);
    try {
      await inviteTenantProfessional(tenantId, professional.id, result.value);
      toast.success(t("admin.tenantTeam.toast.professionalInvited"));
      await loadProfessionals();
      await loadUsers();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error?.message ||
          t("admin.tenantTeam.toast.inviteError")
      );
    } finally {
      setBusyId(null);
    }
  };

  const handleQuickRenameUser = async (user: TenantTeamUser) => {
    const result = await Swal.fire({
      title: t("admin.tenantTeam.editUser.title"),
      input: "text",
      inputValue: user.name,
      showCancelButton: true,
      confirmButtonText: t("admin.tenantTeam.actions.save"),
      cancelButtonText: t("admin.tenantTeam.actions.cancel"),
    });
    if (!result.isConfirmed || !result.value?.trim()) {
      return;
    }
    setBusyId(user.id);
    try {
      await updateTenantUser(tenantId, user.id, { name: result.value.trim() });
      toast.success(t("admin.tenantTeam.toast.userUpdated"));
      await loadUsers();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error?.message ||
          t("admin.tenantTeam.toast.userUpdateError")
      );
    } finally {
      setBusyId(null);
    }
  };

  if (!Number.isFinite(tenantId)) {
    return (
      <div className="page-content">
        <Container fluid>
          <p className="text-danger">{t("admin.tenantTeam.invalidTenant")}</p>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("admin.tenantTeam.breadcrumb.title")}
          pageTitle={t("admin.tenantTeam.breadcrumb.pageTitle")}
        />
        <Row>
          <Col>
            <Card>
              <CardHeader className="d-flex flex-wrap justify-content-between gap-2 align-items-center">
                <div>
                  <h4 className="card-title mb-1">
                    {t("admin.tenantTeam.card.title", {
                      name: tenantName || `#${tenantId}`,
                    })}
                  </h4>
                  <p className="text-muted mb-0">
                    {t("admin.tenantTeam.card.description")}
                  </p>
                </div>
                <Button
                  tag={Link}
                  to={`/super-admin/tenants?tenant_id=${tenantId}`}
                  color="soft-secondary"
                  size="sm"
                >
                  {t("admin.tenantTeam.actions.backToTenant")}
                </Button>
              </CardHeader>
              <CardBody>
                <Nav tabs className="mb-3">
                  <NavItem>
                    <NavLink
                      href="#"
                      className={classnames({ active: tab === "users" })}
                      onClick={(event) => {
                        event.preventDefault();
                        setTab("users");
                      }}
                    >
                      {t("admin.tenantTeam.tabs.users")}
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      href="#"
                      className={classnames({
                        active: tab === "professionals",
                      })}
                      onClick={(event) => {
                        event.preventDefault();
                        setTab("professionals");
                      }}
                    >
                      {t("admin.tenantTeam.tabs.professionals")}
                    </NavLink>
                  </NavItem>
                </Nav>

                {tab === "users" ? (
                  <>
                    <div className="d-flex justify-content-end mb-3">
                      <Button
                        color="primary"
                        size="sm"
                        onClick={() => {
                          void loadProfessionals();
                          setInviteForm((current) => ({
                            ...current,
                            units: unitOptions[0] ? [unitOptions[0].id] : [],
                          }));
                          setInviteOpen(true);
                        }}
                      >
                        {t("admin.tenantTeam.actions.inviteUser")}
                      </Button>
                    </div>
                    {loading ? (
                      <div className="d-flex justify-content-center py-5">
                        <Spinner color="primary" />
                      </div>
                    ) : users.length === 0 ? (
                      <p className="text-muted text-center py-4 mb-0">
                        {t("admin.tenantTeam.empty.users")}
                      </p>
                    ) : (
                      <div className="table-responsive">
                        <Table className="table-nowrap align-middle mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>{t("admin.tenantTeam.table.name")}</th>
                              <th>{t("admin.tenantTeam.table.email")}</th>
                              <th>{t("admin.tenantTeam.table.role")}</th>
                              <th>{t("admin.tenantTeam.table.access")}</th>
                              <th className="text-end">
                                {t("admin.tenantTeam.table.actions")}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {users.map((user) => (
                              <tr key={user.id}>
                                <td>{user.name}</td>
                                <td>{user.email}</td>
                                <td>
                                  {(user.roles ?? [])
                                    .map((role) =>
                                      t(`admin.tenantTeam.roles.${role}`, {
                                        defaultValue: role,
                                      })
                                    )
                                    .join(", ")}
                                </td>
                                <td>
                                  <Badge
                                    color={
                                      user.membership_status === "active"
                                        ? "success"
                                        : user.membership_status === "pending"
                                          ? "warning"
                                          : "secondary"
                                    }
                                    className="fs-12"
                                  >
                                    {t(
                                      `admin.accounts.membershipStatus.${user.membership_status}`,
                                      {
                                        defaultValue:
                                          user.membership_status || "—",
                                      }
                                    )}
                                  </Badge>
                                </td>
                                <td className="text-end">
                                  <div className="d-inline-flex flex-wrap gap-1 justify-content-end">
                                    <Button
                                      color="soft-primary"
                                      size="sm"
                                      disabled={busyId === user.id}
                                      onClick={() =>
                                        void handleQuickRenameUser(user)
                                      }
                                    >
                                      {t("admin.tenantTeam.actions.editName")}
                                    </Button>
                                    <Button
                                      color="soft-success"
                                      size="sm"
                                      disabled={
                                        busyId === user.id ||
                                        user.membership_status === "revoked"
                                      }
                                      onClick={() =>
                                        void handleImpersonate(user)
                                      }
                                    >
                                      {t(
                                        "admin.tenantTeam.actions.impersonate"
                                      )}
                                    </Button>
                                    {!user.roles.includes("owner") ? (
                                      <Button
                                        color="soft-danger"
                                        size="sm"
                                        disabled={
                                          busyId === user.id ||
                                          user.membership_status === "revoked"
                                        }
                                        onClick={() => void handleRevoke(user)}
                                      >
                                        {t("admin.tenantTeam.actions.revoke")}
                                      </Button>
                                    ) : null}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="d-flex flex-wrap justify-content-between gap-2 mb-3">
                      <FormGroup check className="mb-0">
                        <Input
                          id="includeInactive"
                          type="checkbox"
                          checked={includeInactive}
                          onChange={(event) =>
                            setIncludeInactive(event.target.checked)
                          }
                        />
                        <Label check htmlFor="includeInactive">
                          {t("admin.tenantTeam.filters.includeInactive")}
                        </Label>
                      </FormGroup>
                      <Button
                        color="primary"
                        size="sm"
                        onClick={openCreateProfessional}
                      >
                        {t("admin.tenantTeam.actions.createProfessional")}
                      </Button>
                    </div>
                    {loading ? (
                      <div className="d-flex justify-content-center py-5">
                        <Spinner color="primary" />
                      </div>
                    ) : professionals.length === 0 ? (
                      <p className="text-muted text-center py-4 mb-0">
                        {t("admin.tenantTeam.empty.professionals")}
                      </p>
                    ) : (
                      <div className="table-responsive">
                        <Table className="table-nowrap align-middle mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>{t("admin.tenantTeam.table.name")}</th>
                              <th>{t("admin.tenantTeam.table.email")}</th>
                              <th>{t("admin.tenantTeam.table.loginUser")}</th>
                              <th>{t("admin.tenantTeam.table.status")}</th>
                              <th className="text-end">
                                {t("admin.tenantTeam.table.actions")}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {professionals.map((professional) => (
                              <tr key={professional.id}>
                                <td>{professional.name}</td>
                                <td>
                                  {professional.email ||
                                    t("admin.accounts.notAvailable")}
                                </td>
                                <td>
                                  {professional.user?.email ||
                                    t("admin.tenantTeam.professional.noLogin")}
                                </td>
                                <td>
                                  <Badge
                                    color={
                                      professional.is_active
                                        ? "success"
                                        : "secondary"
                                    }
                                    className="fs-12"
                                  >
                                    {professional.is_active
                                      ? t("admin.tenantTeam.status.active")
                                      : t("admin.tenantTeam.status.archived")}
                                  </Badge>
                                </td>
                                <td className="text-end">
                                  <div className="d-inline-flex flex-wrap gap-1 justify-content-end">
                                    <Button
                                      color="soft-primary"
                                      size="sm"
                                      disabled={busyId === professional.id}
                                      onClick={() =>
                                        openEditProfessional(professional)
                                      }
                                    >
                                      {t("admin.tenantTeam.actions.edit")}
                                    </Button>
                                    {!professional.user_id ? (
                                      <Button
                                        color="soft-info"
                                        size="sm"
                                        disabled={
                                          busyId === professional.id ||
                                          !professional.is_active
                                        }
                                        onClick={() =>
                                          void handleInviteProfessional(
                                            professional
                                          )
                                        }
                                      >
                                        {t(
                                          "admin.tenantTeam.actions.inviteLogin"
                                        )}
                                      </Button>
                                    ) : null}
                                    <Button
                                      color="soft-warning"
                                      size="sm"
                                      disabled={busyId === professional.id}
                                      onClick={() =>
                                        void handleArchiveToggle(professional)
                                      }
                                    >
                                      {professional.is_active
                                        ? t("admin.tenantTeam.actions.archive")
                                        : t("admin.tenantTeam.actions.restore")}
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal isOpen={inviteOpen} toggle={() => setInviteOpen(false)}>
        <ModalHeader toggle={() => setInviteOpen(false)}>
          {t("admin.tenantTeam.invite.title")}
        </ModalHeader>
        <ModalBody>
          <p className="text-muted small">
            {t("admin.tenantTeam.invite.help")}
          </p>
          <FormGroup>
            <Label>{t("admin.tenantTeam.invite.name")}</Label>
            <Input
              value={inviteForm.name}
              onChange={(event) =>
                setInviteForm((current) => ({
                  ...current,
                  name: event.target.value,
                }))
              }
            />
          </FormGroup>
          <FormGroup>
            <Label>{t("admin.tenantTeam.invite.email")}</Label>
            <Input
              type="email"
              value={inviteForm.email}
              onChange={(event) =>
                setInviteForm((current) => ({
                  ...current,
                  email: event.target.value,
                }))
              }
            />
          </FormGroup>
          <FormGroup>
            <Label>{t("admin.tenantTeam.invite.role")}</Label>
            <Input
              type="select"
              value={inviteForm.role}
              onChange={(event) =>
                setInviteForm((current) => ({
                  ...current,
                  role: event.target.value,
                }))
              }
            >
              {INVITABLE_ROLES.map((role) => (
                <option key={role} value={role}>
                  {t(`admin.tenantTeam.roles.${role}`, { defaultValue: role })}
                </option>
              ))}
            </Input>
          </FormGroup>
          <FormGroup>
            <Label>{t("admin.tenantTeam.invite.units")}</Label>
            {unitOptions.length === 0 ? (
              <p className="text-muted small mb-0">
                {t("admin.tenantTeam.invite.unitsEmpty")}
              </p>
            ) : (
              <div className="border rounded p-2 d-flex flex-column gap-1">
                <p className="text-muted small mb-1">
                  {t("admin.tenantTeam.invite.unitsHint")}
                </p>
                {unitOptions.map((unit) => {
                  const checked = inviteForm.units.includes(unit.id);
                  return (
                    <FormGroup check key={unit.id} className="mb-0">
                      <Input
                        id={`invite-unit-${unit.id}`}
                        type="checkbox"
                        checked={checked}
                        onChange={() =>
                          setInviteForm((current) => ({
                            ...current,
                            units: checked
                              ? current.units.filter((id) => id !== unit.id)
                              : [...current.units, unit.id],
                          }))
                        }
                      />
                      <Label check htmlFor={`invite-unit-${unit.id}`}>
                        {unit.name}
                      </Label>
                    </FormGroup>
                  );
                })}
              </div>
            )}
          </FormGroup>
          {inviteForm.role === "professional" ? (
            <FormGroup>
              <Label>{t("admin.tenantTeam.invite.professional")}</Label>
              <Input
                type="select"
                value={inviteForm.professional_id}
                onChange={(event) =>
                  setInviteForm((current) => ({
                    ...current,
                    professional_id: event.target.value,
                  }))
                }
              >
                <option value="">
                  {t("admin.tenantTeam.invite.professionalPlaceholder")}
                </option>
                {professionals
                  .filter((item) => item.is_active && !item.user_id)
                  .map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
              </Input>
            </FormGroup>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setInviteOpen(false)}>
            {t("admin.tenantTeam.actions.cancel")}
          </Button>
          <Button
            color="primary"
            disabled={busyId === -1}
            onClick={() => void handleInviteSubmit()}
          >
            {busyId === -1 ? <Spinner size="sm" /> : t("admin.tenantTeam.invite.submit")}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={proModalOpen} toggle={() => setProModalOpen(false)}>
        <ModalHeader toggle={() => setProModalOpen(false)}>
          {editingPro
            ? t("admin.tenantTeam.professional.editTitle")
            : t("admin.tenantTeam.professional.createTitle")}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>{t("admin.tenantTeam.table.name")}</Label>
            <Input
              value={proForm.name}
              onChange={(event) =>
                setProForm((current) => ({
                  ...current,
                  name: event.target.value,
                }))
              }
            />
          </FormGroup>
          <FormGroup>
            <Label>{t("admin.tenantTeam.table.email")}</Label>
            <Input
              type="email"
              value={proForm.email}
              onChange={(event) =>
                setProForm((current) => ({
                  ...current,
                  email: event.target.value,
                }))
              }
            />
          </FormGroup>
          <FormGroup>
            <Label>{t("admin.tenantTeam.professional.color")}</Label>
            <Input
              type="color"
              value={proForm.color_hex}
              onChange={(event) =>
                setProForm((current) => ({
                  ...current,
                  color_hex: event.target.value,
                }))
              }
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setProModalOpen(false)}>
            {t("admin.tenantTeam.actions.cancel")}
          </Button>
          <Button
            color="primary"
            disabled={busyId === -2}
            onClick={() => void handleSaveProfessional()}
          >
            {busyId === -2 ? (
              <Spinner size="sm" />
            ) : (
              t("admin.tenantTeam.actions.save")
            )}
          </Button>
        </ModalFooter>
      </Modal>

      <ToastContainer closeButton={false} limit={1} />
    </div>
  );
};

export default SuperAdminTenantTeam;
